import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Viewproof } from "../Viewproof/Viewproof";

// Service types this review screen knows how to handle.
// Anything not in here renders a safe "unconfigured" panel instead of
// silently falling into the address form (which caused the white-screen crash).
const KNOWN_TYPES = [
  "NEFT",
  "ADDRESS",
  "COMMUNICATION_ADDRESS",
  "FACEMATCH",
  "EMAIL_KYC",
  "MOBILE_KYC",
];

const REJECT_REASONS = [
  { value: "proof Not valid", label: "Proof not clear" },
  { value: "Name missmatch with our Record", label: "Name mismatch with our record" },
  { value: "Name given and the name on proof mismatch", label: "Name given and name on proof mismatch" },
  { value: "Submitted proof not valid", label: "Submitted proof not valid" },
  { value: "other", label: "Other" },
];

const TransactionModal = ({
  showModal,
  handleClose,
  selectedRow,
  handleMaualstatusChange,
  handleSubmit,
  currentpage,
  handleRejectCommentsChange,
  handleRejectReasonChange,
}) => {
  const type = (selectedRow?.serviceType || "").toUpperCase();
  const isKnownType = KNOWN_TYPES.includes(type);
  const isFaceMatch = type === "FACEMATCH";

  // Only allow submitting on the "new" queue AND when the type is one we can
  // build a valid payload for. Unknown types are view-only until configured.
  const canSubmit = currentpage === "new" && isKnownType;

  // FaceMatch footer decision state (radios + reject popup).
  const [fmChoice, setFmChoice] = useState(""); // "approve" | "reject"
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [popupReason, setPopupReason] = useState("");
  const [popupComments, setPopupComments] = useState("");

  // Reset footer/popup state whenever a different row opens or the modal closes.
  useEffect(() => {
    setFmChoice("");
    setShowRejectPopup(false);
    setPopupReason("");
    setPopupComments("");
  }, [selectedRow, showModal]);

  // FaceMatch approve: build the Approved payload here (FaceMatch has no
  // address/bank fields to carry, so the empty positional args are correct),
  // then fire the parent's submit.
  const handleFaceMatchApprove = () => {
    handleMaualstatusChange(
      "Approved",
      "", "", "", "", "", "", selectedRow?.country || "",
      "", "", "", "",
      selectedRow?.msgId,
      selectedRow?.kycProofId,
      selectedRow?.poaCode,
      selectedRow?.pobCode
    );
    // Pass the decision explicitly: handleMaualstatusChange's setState hasn't
    // flushed yet, so handleSubmit() reading state alone would see "" and fail
    // validation on the first click.
    handleSubmit({ status: "Approved" });
  };

  // FaceMatch reject: confirmed from the popup. Sets reason/comments via the
  // existing callbacks, builds the Rejected payload, then submits.
  const handleFaceMatchRejectConfirm = () => {
    handleRejectReasonChange(popupReason);
    handleRejectCommentsChange(popupComments);
    handleMaualstatusChange(
      "Rejected",
      "", "", "", "", "", "", selectedRow?.country || "",
      "", "", "", "",
      selectedRow?.msgId,
      selectedRow?.kycProofId,
      selectedRow?.poaCode,
      selectedRow?.pobCode
    );
    // Same reason as approve: thread the reject decision through directly so
    // the first click doesn't read stale (empty) state.
    handleSubmit({ status: "Rejected", rejectReason: popupReason, rejectComments: popupComments });
    setShowRejectPopup(false);
  };

  const onFmChoice = (value) => {
    setFmChoice(value);
    if (value === "reject") {
      setShowRejectPopup(true);
    } else {
      setShowRejectPopup(false);
    }
  };

  const rejectDisabled = !popupReason || (popupReason === "other" && !popupComments);

  return (
    <>
      <Modal
        show={showModal}
        onHide={handleClose}
        dialogClassName="vp-modal"
        centered
      >
        <Modal.Body className="vp-modal-body">
          {selectedRow && (
            <Viewproof
              rowData={selectedRow}
              onManualStatusChange={handleMaualstatusChange}
              onRejectReasonChange={handleRejectReasonChange}
              onRejectCommentsChange={handleRejectCommentsChange}
            />
          )}
        </Modal.Body>

        <Modal.Footer className="vp-modal-footer">
          {/* FaceMatch: decision radios live here in the footer. */}
          {canSubmit && isFaceMatch && (
            <div className="vp-footer-decision">
              <label className="vp-radio">
                <input
                  type="radio"
                  name="fm-decision"
                  value="approve"
                  checked={fmChoice === "approve"}
                  onChange={() => onFmChoice("approve")}
                />
                Approve
              </label>
              <label className="vp-radio">
                <input
                  type="radio"
                  name="fm-decision"
                  value="reject"
                  checked={fmChoice === "reject"}
                  onChange={() => onFmChoice("reject")}
                />
                Reject
              </label>
            </div>
          )}

          {/* FaceMatch approve uses a dedicated submit; reject is driven by the popup. */}
          {canSubmit && isFaceMatch && (
            <Button
              className="vp-btn vp-btn-primary"
              disabled={fmChoice !== "approve"}
              onClick={handleFaceMatchApprove}
            >
              Submit decision
            </Button>
          )}

          {/* Non-FaceMatch keeps the original single submit button. */}
          {canSubmit && !isFaceMatch && (
            <Button className="vp-btn vp-btn-primary" onClick={() => handleSubmit()}>
              Submit decision
            </Button>
          )}

          <Button className="vp-btn vp-btn-ghost" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* FaceMatch reject popup: reason dropdown (+ details for "Other"),
          and a Reject button that submits via the parent's handleSubmit. */}
      <Modal
        show={showRejectPopup}
        onHide={() => {
          setShowRejectPopup(false);
          setFmChoice("");
        }}
        dialogClassName="vp-reject-modal"
        centered
      >
        <Modal.Body className="vp-reject-modal-body">
          <h2 className="vp-reject-modal-title">Reject FaceMatch</h2>
          <div className="vp-field">
            <label>Reject reason <span className="vp-req">*</span></label>
            <select
              value={popupReason}
              onChange={(e) => setPopupReason(e.target.value)}
            >
              <option value="">Select a reason</option>
              {REJECT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          {popupReason === "other" && (
            <div className="vp-field">
              <label>Reject reason (details) <span className="vp-req">*</span></label>
              <input
                type="text"
                value={popupComments}
                onChange={(e) => setPopupComments(e.target.value)}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="vp-reject-modal-footer">
          <Button
            className="vp-btn vp-btn-reject"
            disabled={rejectDisabled}
            onClick={handleFaceMatchRejectConfirm}
          >
            Reject
          </Button>
          <Button
            className="vp-btn vp-btn-ghost"
            onClick={() => {
              setShowRejectPopup(false);
              setFmChoice("");
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TransactionModal;